import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"

/**
 * Node types that mark the start of a new content block. A question heading's
 * answer runs from the heading up to (but not including) the next heading.
 */
const HEADING_NODE_TYPES = ["heading-2", "heading-3"]

/**
 * Convert one or more rich-text nodes into a trimmed plain-text string by
 * wrapping them in a synthetic document node.
 */
function nodesToPlainText(nodes) {
  return documentToPlainTextString({
    nodeType: "document",
    data: {},
    content: Array.isArray(nodes) ? nodes : [nodes],
  }).trim()
}

/**
 * A heading counts as a question when its text is phrased as one. We rely on a
 * trailing question mark, which is the most reliable signal for Dutch content.
 */
function isQuestion(text) {
  return typeof text === "string" && text.trim().endsWith("?")
}

/**
 * Extract FAQ question/answer pairs from a Contentful rich-text body.
 *
 * A question is any H2/H3 heading phrased as a question; its answer is every
 * following block up to the next H2/H3 heading.
 *
 * @param {string|object} rawBody - The `body.raw` JSON string (or parsed document).
 * @returns {Array<{ question: string, answer: string }>} Ordered FAQ items.
 */
export function extractFaqItems(rawBody) {
  if (!rawBody) return []

  let document
  try {
    document = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody
  } catch (e) {
    return []
  }

  const content = document && document.content
  if (!Array.isArray(content)) return []

  const items = []

  for (let i = 0; i < content.length; i++) {
    const node = content[i]
    if (!node || !HEADING_NODE_TYPES.includes(node.nodeType)) continue

    const question = nodesToPlainText(node)
    if (!isQuestion(question)) continue

    // Collect the answer: everything until the next heading.
    const answerNodes = []
    for (let j = i + 1; j < content.length; j++) {
      if (HEADING_NODE_TYPES.includes(content[j].nodeType)) break
      answerNodes.push(content[j])
    }
    if (!answerNodes.length) continue

    const answer = nodesToPlainText(answerNodes)
    if (!answer) continue

    items.push({ question, answer })
  }

  return items
}

/**
 * Build a schema.org FAQPage JSON-LD object from extracted FAQ items.
 *
 * @param {Array<{ question: string, answer: string }>} items
 * @returns {object|null} The FAQPage object, or null when there are no items.
 */
export function buildFaqPageJsonLd(items) {
  if (!items || !items.length) return null

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  }
}

/**
 * Convenience helper: derive the FAQPage JSON-LD directly from a rich-text body.
 *
 * @param {string|object} rawBody - The `body.raw` JSON string (or parsed document).
 * @returns {object|null} The FAQPage object, or null when no question headings exist.
 */
export function faqPageJsonLdFromRichText(rawBody) {
  return buildFaqPageJsonLd(extractFaqItems(rawBody))
}
